<template>
    <div class="app-nav-drawer">
        <!-- escritorio: sidebar completo (o rail si se colapsó); tablet: rail fijo -->
        <v-navigation-drawer v-if="mode !== 'overlay'" app clipped permanent class="app-drawer"
            :mini-variant="isMini" mini-variant-width="56" width="240">
            <app-nav-list :nav="nav" :mini="isMini" @expand="expand"></app-nav-list>
            <template v-slot:append>
                <app-nav-user :user="user" :avatar-url="avatarUrl" :mini="isMini"></app-nav-user>
            </template>
        </v-navigation-drawer>

        <!-- tablet y teléfono: el menú completo se abre encima del contenido -->
        <v-navigation-drawer v-if="mode !== 'full'" app temporary width="280" class="app-drawer"
            :value="open" @input="$emit('update:open', $event)">
            <app-nav-list :nav="nav"></app-nav-list>
            <template v-slot:append>
                <app-nav-user :user="user" :avatar-url="avatarUrl"></app-nav-user>
            </template>
        </v-navigation-drawer>
    </div>
</template>
<script>
import AppNavList from '@/components/shared/AppNavList.vue';
import AppNavUser from '@/components/shared/AppNavUser.vue';
import { navModeFor } from '@/service/permiso/menuModel';

/**
 * Menú lateral responsivo (un solo componente para los tres anchos):
 *
 * | Ancho            | `mode`    | Qué se ve                                      |
 * |------------------|-----------|------------------------------------------------|
 * | lg y xl          | `full`    | sidebar de 240px; la hamburguesa lo pasa a rail |
 * | sm y md (tablet) | `rail`    | rail de 56px; la hamburguesa abre el completo   |
 * | xs (teléfono)    | `overlay` | solo drawer temporal                            |
 *
 * `open` es el drawer temporal; `mini` es la preferencia del escritorio.
 * Migración a Vuetify 3: `mini-variant` -> `rail`, `mini-variant-width` ->
 * `rail-width`, `app clipped` sigue igual, y el nombre del breakpoint sale de
 * `useDisplay().name` en vez de `$vuetify.breakpoint.name`. Es el único
 * archivo con esa dependencia; la lista y el modelo no cambian.
 */
export default {
    name: 'AppNavDrawer',
    components: { AppNavList, AppNavUser },
    props: {
        nav: { type: Array, default: () => [] },
        user: { type: Object, default: () => ({}) },
        avatarUrl: { type: String, default: '' },
        open: { type: Boolean, default: false },
        mini: { type: Boolean, default: false }
    },
    computed: {
        mode() {
            return navModeFor(this.$vuetify.breakpoint.name);
        },
        isMini() {
            return this.mode === 'rail' || this.mini;
        }
    },
    watch: {
        // navegar cierra el menú temporal
        '$route.fullPath'() {
            if (this.open) this.$emit('update:open', false);
        },
        // al cambiar de ancho no debe quedar un drawer temporal abierto
        mode() {
            if (this.open) this.$emit('update:open', false);
        }
    },
    methods: {
        /** La hamburguesa: colapsa/expande en escritorio, abre/cierra en el resto. */
        toggle() {
            if (this.mode === 'full') {
                this.$emit('update:mini', !this.mini);
            } else {
                this.$emit('update:open', !this.open);
            }
        },
        /** Se tocó un grupo en el rail: no hay donde desplegarlo, abre el menú completo. */
        expand() {
            if (this.mode === 'full') {
                this.$emit('update:mini', false);
            } else {
                this.$emit('update:open', true);
            }
        }
    }
};
</script>
<style scoped>
/* verde salvia del menú (Vuetify pinta el drawer con el fondo de su tema, claro u oscuro) */
.theme--light.v-navigation-drawer.app-drawer,
.theme--dark.v-navigation-drawer.app-drawer {
    background-color: var(--app-nav-bg);
}
</style>
